/**
 * RmsSetUpPassword.js
 *
 * Password setup page for newly invited RMS provider users.
 *
 * When an admin invites a new provider via the Staff Dashboard, the backend
 * creates a Firebase Auth account with a temporary password and sends a
 * welcome email. The email does NOT expose the temp password — instead it
 * links directly here with `?setup=true&e={base64Email}&t={setupToken}`.
 *
 * The single-use token proves the visitor holds the emailed link; the email
 * alone is not enough. Sign-in also redirects a signed-in user who has not
 * set their own password here without a token, and the server accepts that
 * because the caller is already signed in as the account.
 *
 * This page lets the user create a permanent password without ever needing
 * to know the temporary one. On submit it calls the RMS
 * `rmsCompleteProviderPasswordSetup` Cloud Function, which:
 *   - Checks the setup token (or that the caller is signed in as the account)
 *   - Validates password strength server-side
 *   - Updates the Firebase Auth password via Admin SDK
 *   - Sets `hasSetOwnPassword=true` in custom claims and consumes the invite
 *
 * After success, the user is redirected to RmsProviderSignIn to sign in
 * with their new password and continue through MFA setup.
 *
 * This mirrors the ADHD module's SetUpPassword.js but uses RMS branding
 * and routes back to the RMS provider sign-in page.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, analytics, functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../../analytics/ga4';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import PasswordRequirements from '../../../components/auth/PasswordRequirements';
import { validatePasswordString } from '../../../hooks/auth/usePasswordValidation';
import InfoTooltip from '../../../components/InfoTooltip';
import providerLogo from '../../assets/images/logos/lcs_logo.png';

// One message for every unusable link (missing, wrong, used or expired
// token), matching the server, which does not say which it was.
const INVALID_LINK_MESSAGE =
  'This setup link is not valid or has expired. Please contact your organisation administrator for a new invitation.';

export default function RmsSetUpPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Password visibility
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(null);

  // Environment banner — shows in dev/staging to help identify the environment
  const showEnvBanner =
    import.meta.env.VITE_ENVIRONMENT === 'development' ||
    import.meta.env.VITE_ENVIRONMENT === 'staging';
  const envBannerText =
    import.meta.env.VITE_ENVIRONMENT === 'development'
      ? 'Development Environment'
      : 'Testing / Training Environment';
  const envBannerClass =
    import.meta.env.VITE_ENVIRONMENT === 'development'
      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
      : 'bg-blue-100 text-blue-800 border-blue-200';

  const handleNewPasswordChange = (e) => {
    const val = e.target.value;
    setNewPassword(val);
    if (confirmPassword) setPasswordMatch(confirmPassword === val);
  };

  const handleConfirmPasswordChange = (e) => {
    const val = e.target.value;
    setConfirmPassword(val);
    setPasswordMatch(val === newPassword);
  };

  // ── Initialise: decode & validate the email from the URL ──────────────
  useEffect(() => {
    const initializeSetup = async () => {
      const encodedEmail = searchParams.get('e');
      const token = searchParams.get('t') || '';
      const isSetup = searchParams.get('setup') === 'true';

      if (!encodedEmail || !isSetup) {
        setError('Invalid setup link. Please contact your organisation administrator.');
        setErrorCode('CP-001L');
        setIsLinkInvalid(true);
        logEvent(analytics, 'rms_password_setup_invalid_link', { error: 'missing_params' });
        setIsInitializing(false);
        return;
      }

      try {
        const decoded = atob(encodedEmail);
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(decoded)) throw new Error('Invalid email format');
        setEmail(decoded);
        setSetupToken(token);

        // Upfront server-side check: an invalid, used or expired link, or an
        // account that is already set up, fails here before the user types.
        try {
          const validateSetup = httpsCallable(functions, 'rmsValidateProviderPasswordSetup');
          await validateSetup({ email: decoded, token });
        } catch (validationError) {
          const code = validationError?.code || '';
          const msg = validationError?.message || '';
          if (code === 'functions/failed-precondition' && msg.toLowerCase().includes('already')) {
            logEvent(analytics, 'rms_password_setup_already_complete', {});
            navigate('/rms/provider-sign-in?message=account_already_setup');
            return;
          }
          if (code === 'functions/not-found') {
            setError(INVALID_LINK_MESSAGE);
            setErrorCode('CP-001X');
            setIsLinkInvalid(true);
            logEvent(analytics, 'rms_password_setup_invalid_link', { error: 'rejected_by_server' });
            return;
          }
          // Transient errors (network, internal) fall through and let the
          // user attempt the form; the submit enforces the same gates.
        }
      } catch (err) {
        setError('Invalid setup link. Please contact your organisation administrator.');
        setErrorCode('CP-001L');
        setIsLinkInvalid(true);
        logEvent(analytics, 'rms_password_setup_invalid_link', {
          error_type: err.name,
        });
      } finally {
        setIsInitializing(false);
      }
    };

    initializeSetup();
  }, [searchParams, navigate]);

  // ── Submit handler ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setErrorCode('CP-001M');
      setIsLoading(false);
      return;
    }

    const { errors: validationErrors } = validatePasswordString(newPassword);
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setErrorCode('CP-001P');
      setIsLoading(false);
      return;
    }

    try {
      const fn = httpsCallable(functions, 'rmsCompleteProviderPasswordSetup');
      await fn({ email, token: setupToken, newPassword });

      logEvent(analytics, 'rms_provider_password_setup_success', {});

      // Redirect to the RMS provider sign-in page with a success banner and
      // the email pre-filled so the user can sign in immediately.
      const encoded = btoa(email);
      navigate(`/rms/provider-sign-in?message=setup_complete&e=${encodeURIComponent(encoded)}`);
    } catch (err) {
      let message = 'An error occurred. Please try again.';
      let code = 'CP-001G';

      if (err.code === 'functions/invalid-argument') {
        message = err.message?.includes('Password must')
          ? err.message
          : 'Please check your password requirements and try again.';
        code = 'CP-001P';
      } else if (err.code === 'functions/not-found') {
        message = INVALID_LINK_MESSAGE;
        code = 'CP-001X';
        setIsLinkInvalid(true);
      } else if (err.code === 'functions/permission-denied') {
        message = 'Invalid account type for this setup process.';
        code = 'CP-001D';
      } else if (err.code === 'functions/failed-precondition') {
        if (err.message?.includes('Account already set up')) {
          message = 'Account already set up. Please sign in with your password.';
          code = 'CP-001A';
          setTimeout(
            () => navigate(`/rms/provider-sign-in?message=account_already_setup`),
            2000,
          );
        } else if (err.message?.includes('Email verification required')) {
          message = 'Email verification required. Please contact your organisation administrator.';
          code = 'CP-001V';
        } else {
          message = err.message;
        }
      } else if (err.code === 'functions/internal') {
        message =
          'A technical error occurred. Please try again or contact your organisation administrator.';
        code = 'CP-001T';
      } else if (err.message?.includes('Password must')) {
        message = err.message;
        code = 'CP-001P';
      }

      setError(message);
      setErrorCode(code);
      logEvent(analytics, 'rms_provider_password_setup_error', {
        error_code: err.code,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Loading state while we validate the URL params ────────────────────
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm text-center">
          <div className="flex justify-center mb-10">
            <img src={providerLogo} alt="Provider Logo" className="h-12 w-auto" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Initialising...</h2>
          <p className="text-gray-600 mb-6">Please wait while we verify your setup link.</p>
          <div className="animate-spin mx-auto h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Environment banner */}
        {showEnvBanner && (
          <div
            className={`mb-10 px-4 py-2 rounded-lg border text-center text-sm font-medium ${envBannerClass}`}
          >
            {envBannerText}
          </div>
        )}

        {/* Logo */}
        <div className="flex justify-center mb-10">
          <img src={providerLogo} alt="Provider Logo" className="h-12 w-auto" />
        </div>

        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Set Up Your Account</h1>
          <p className="text-gray-600 mb-8">
            Please create a secure password for your account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="text-left">
          {/* Email (read-only, pre-filled from the invite link) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              readOnly
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 focus:outline-none text-base"
            />
          </div>

          {/* New Password */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <InfoTooltip
                side="right"
                content="Your password needs to have 12 characters or more, and contain at least 1 number, 1 symbol and 1 upper & lower case letter"
              />
            </div>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handleNewPasswordChange}
                required
                placeholder="Enter your secure password"
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-base placeholder-gray-400 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                tabIndex={-1}
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              >
                <span className="relative h-5 w-5">
                  <EyeIcon
                    className={`absolute inset-0 h-5 w-5 transition-all duration-150 ${
                      showNewPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                    }`}
                  />
                  <EyeOffIcon
                    className={`absolute inset-0 h-5 w-5 transition-all duration-150 ${
                      showNewPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                    }`}
                  />
                </span>
              </button>
            </div>
            <PasswordRequirements
              value={newPassword}
              onValidityChange={setIsPasswordValid}
              className="mt-2"
            />
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
                placeholder="Confirm your password"
                disabled={!newPassword || !isPasswordValid}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary text-base placeholder-gray-400 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={!newPassword || !isPasswordValid}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                tabIndex={-1}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                <span className="relative h-5 w-5">
                  <EyeIcon
                    className={`absolute inset-0 h-5 w-5 transition-all duration-150 ${
                      showConfirmPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                    }`}
                  />
                  <EyeOffIcon
                    className={`absolute inset-0 h-5 w-5 transition-all duration-150 ${
                      showConfirmPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                    }`}
                  />
                </span>
              </button>
            </div>
            {/* Match indicator */}
            <div
              className={`mt-1 overflow-hidden transition-all duration-300 ${
                passwordMatch !== null ? 'max-h-6' : 'max-h-0'
              }`}
            >
              <div className="flex items-start text-sm">
                <span
                  className={`mr-2 font-bold ${
                    passwordMatch ? 'text-green-600' : 'text-gray-500'
                  }`}
                >
                  {passwordMatch ? '✔' : '✘'}
                </span>
                <span className={passwordMatch ? 'text-green-700' : 'text-gray-700'}>
                  {passwordMatch ? 'Passwords match' : 'Passwords do not match'}
                </span>
              </div>
            </div>
          </div>

          {/* Error banner */}
          <div
            className={`transition-all duration-300 overflow-hidden ${
              error ? 'max-h-40 mb-4' : 'max-h-0'
            }`}
            role="alert"
          >
            <div className="text-sm text-red-600 bg-red-50 rounded-lg py-3 px-4 text-left">
              <div className="flex items-start">
                <svg
                  className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  {error}
                  {errorCode ? ` (Code: ${errorCode})` : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLinkInvalid || isLoading || !isPasswordValid || !passwordMatch}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all flex items-center justify-center ${
              isLinkInvalid || isLoading || !isPasswordValid || !passwordMatch
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg'
            }`}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Setting up your account...
              </>
            ) : (
              'Set Up Password'
            )}
          </button>
        </form>

        {/* Support footer */}
        <div className="mt-8 text-center">
          {(() => {
            const subject = 'Reporting Issue with ThinkDivergent RMS - Set Up Password';
            const now = new Date();
            const day = now.getDate();
            const sfx = (d) => {
              if (d % 100 >= 11 && d % 100 <= 13) return 'th';
              switch (d % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
              }
            };
            const easyTime = `${day}${sfx(day)} ${now.toLocaleString('en-GB', {
              month: 'long',
            })} ${now.getFullYear()} at ${now.toLocaleString('en-GB', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}`;
            const isoTime = now.toISOString();
            const userId = auth?.currentUser?.uid || '';
            const lines = [
              'To ThinkDivergent Support Team,',
              '',
              'I would like to report an issue on the RMS platform.',
              '',
              '[Please describe briefly what you were doing and what happened]',
              '',
              'Helpful Information:',
              `- Screen: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
              `- URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
              `- Time: ${easyTime} (${isoTime})`,
              userId ? `- User ID: ${userId}` : undefined,
              error ? `- Message: ${error}` : undefined,
              `- Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
            ]
              .filter((v) => v != null)
              .join('\n');
            const href = `mailto:support@thinkdivergent.co.uk?subject=${encodeURIComponent(
              subject,
            )}&body=${encodeURIComponent(lines)}`;
            return (
              <p className="text-xs text-gray-500">
                Need help? Contact your organisation administrator or{' '}
                <a href={href} className="underline text-primary hover:text-primary/80">
                  report an issue
                </a>
              </p>
            );
          })()}
        </div>

        {/* Footer partnership note */}
        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-600">
          <span>Made in partnership with</span>
          <img
            src="/td_logo_full_2025.png"
            alt="ThinkDivergent"
            className="h-6 w-auto opacity-90"
          />
        </div>
      </div>
    </div>
  );
}
